import {
  type AirActionTrackingLocation,
  type EditInCanvasSubmenuOpenedFrom,
  type UseTrackCanvasCreatedParams,
  type UseTrackCanvasViewedParams,
  type UseTrackEditInCanvasOpenedParams,
} from '@air/analytics';

import { type PendingCanvasEditRequest } from '~/components/v2/store/features/canvas/usePendingCanvasEditStore';

export type EditInCanvasOpenedFrom = UseTrackEditInCanvasOpenedParams['opened_from'];

type EditInCanvasFunnelParams = {
  source: NonNullable<UseTrackCanvasCreatedParams['source']>;
  viewedFrom: NonNullable<UseTrackCanvasViewedParams['viewed_from']>;
};

export const getEditInCanvasOpenedFrom = (trackLocation: AirActionTrackingLocation): EditInCanvasSubmenuOpenedFrom => {
  switch (trackLocation) {
    case 'action-bar':
      return 'action_bar';
    case 'gallery-item-context-menu':
      return 'gallery_item_context_menu';
    case 'gallery-item-ellipsis-menu':
      return 'gallery_item_ellipsis_menu';
    case 'gallery-multi-select-context-menu':
      return 'gallery_multi_select_context_menu';
    case 'gallery-multi-select-ellipsis-menu':
      return 'gallery_multi_select_ellipsis_menu';
    case 'table-item-context-menu':
      return 'table_item_context_menu';
    case 'table-item-ellipsis-menu':
      return 'table_item_ellipsis_menu';
    case 'table-multi-select-context-menu':
      return 'table_multi_select_context_menu';
    case 'table-multi-select-ellipsis-menu':
      return 'table_multi_select_ellipsis_menu';
    default:
      return 'asset_context_menu';
  }
};

export const getEditInCanvasOpenedAction = (
  action: PendingCanvasEditRequest['action'],
): UseTrackEditInCanvasOpenedParams['action'] => {
  switch (action) {
    case 'adjustImage':
      return 'adjust_image';
    case 'cameraAngle':
      return 'camera_angle';
    case 'cameraMotion':
      return 'camera_motion';
    case 'changeLighting':
      return 'change_lighting';
    case 'crop':
      return 'crop';
    case 'editTextOcr':
      return 'edit_text';
    case 'extendBackground':
      return 'extend_background';
    case 'imageToVideo':
      return 'image_to_video';
    case 'open':
      return 'open';
    case 'prompt':
      return 'prompt';
    case 'removeBackground':
      return 'remove_background';
    case 'removeObject':
      return 'erase';
    case 'resizeForSet':
      return 'resize_for_set';
    case 'rotateFlip':
      return 'flip_and_rotate';
    case 'smartResize':
      return 'smart_resize';
    case 'solidBackground':
      return 'add_solid_background';
    case 'splitElements':
      return 'split_elements';
    case 'translate':
      return 'translate';
    case 'upscale':
      return 'upscale';
    case 'videoConvert':
      return 'convert_to_gif';
    case 'videoExtend':
      return 'extend_video';
  }
};

export const getEditInCanvasFunnelParams = (openedFrom: EditInCanvasOpenedFrom): EditInCanvasFunnelParams => {
  switch (openedFrom) {
    case 'announcement':
      return { source: 'announcement', viewedFrom: 'announcement' };
    case 'asset_card_hover':
      return { source: 'asset_hover', viewedFrom: 'asset_hover' };
    case 'action_bar':
    case 'action_bar_export_as':
      return { source: 'asset_selection_bar', viewedFrom: 'asset_selection_bar' };
    case 'gallery_multi_select_context_menu':
    case 'gallery_multi_select_ellipsis_menu':
    case 'gallery_multi_select_export_as':
    case 'table_multi_select_context_menu':
    case 'table_multi_select_ellipsis_menu':
    case 'table_multi_select_export_as':
      return { source: 'asset_multi_select', viewedFrom: 'asset_multi_select' };
    case 'asset_modal_quick_tools':
      return { source: 'asset_modal_quick_tools', viewedFrom: 'asset_modal_quick_tools' };
    case 'asset_modal_versions_menu':
      return { source: 'asset_modal_versions_menu', viewedFrom: 'asset_modal_versions_menu' };
    case 'asset_modal_video_editor':
      return { source: 'asset_modal_video_editor', viewedFrom: 'asset_modal_video_editor' };
    case 'asset_context_menu':
    case 'asset_modal_export_as':
    case 'asset_modal_header_menu':
    case 'gallery_item_context_menu':
    case 'gallery_item_ellipsis_menu':
    case 'gallery_item_export_as':
    case 'table_item_context_menu':
    case 'table_item_ellipsis_menu':
    case 'table_item_export_as':
      return { source: 'asset_context_menu', viewedFrom: 'asset_context_menu' };
  }
};
